import { APPLICATION_ROUTES, THUMBNAILS_BASE_URL } from '@/constants/constants';
import queryKeys from '@/constants/queryKeys';
import {
  useDuplicateTemplateMutation,
  useRenameTemplateMutation,
  useSelectTemplateMutation,
  useValidateDeleteTemplateMutation,
} from '@/hooks/api/template-gallery';
import { useGetBoardNameAndColumnsQuery } from '@/hooks/api/useGetBoardNameAndColumnsQuery';
import useGetContextQuery from '@/hooks/api/useGetContextQuery';
import { useTemplateGalleryContext } from '@/hooks/useTemplateGalleryContext';
import { openLinkInTab } from '@/utils/monday';
import {
  Button,
  EditableText,
  Icon,
  IconButton,
  Skeleton,
  Tooltip,
} from '@vibe/core';
import { Delete, Doc, Duplicate, Edit, Show } from '@vibe/icons';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { TemplateGalleryUserTemplate } from './UserTemplates';
import { FEATURE_FLAGS, isFeatureEnabled } from '@/constants/featureFlags';

interface TemplateCardProps {
  template: TemplateGalleryUserTemplate;
  envelopeId: string;
}

const TemplateCard = (props: TemplateCardProps) => {
  const { template, envelopeId } = props;
  const { data: context } = useGetContextQuery();
  const { data: boardDetails, isLoading: isBoardDetailsLoading } =
    useGetBoardNameAndColumnsQuery(template?.boardId);
  const boardName = boardDetails?.name || 'Board Deleted';
  const { hideGallery, showDeleteTemplateDialog } = useTemplateGalleryContext();
  const queryClient = useQueryClient();
  const [isSelecting, setIsSelecting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { mutate: renameTemplate } = useRenameTemplateMutation(template._id, {
    onSuccess: async (newData: { templateName: string }) => {
      await queryClient.cancelQueries(
        queryKeys.TEMPLATE_GALLERY_USER_TEMPLATES,
      );
      const previousData = queryClient.getQueryData<any>(
        queryKeys.TEMPLATE_GALLERY_USER_TEMPLATES,
      );
      queryClient.setQueryData(
        queryKeys.TEMPLATE_GALLERY_USER_TEMPLATES,
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              rows: page.rows.map((t: any) =>
                t._id === template._id
                  ? { ...t, templateName: newData.templateName }
                  : t,
              ),
            })),
          };
        },
      );
      return { previousData };
    },
    onError: (err: unknown, _variables: unknown, context: any) => {
      if (context && typeof context === 'object' && 'previousData' in context) {
        queryClient.setQueryData(
          queryKeys.TEMPLATE_GALLERY_USER_TEMPLATES,
          context.previousData,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(queryKeys.TEMPLATE_GALLERY_USER_TEMPLATES);
    },
  });
  const { mutateAsync: validateDeleteTemplate } =
    useValidateDeleteTemplateMutation(template._id);
  const { mutateAsync: duplicateTemplate } = useDuplicateTemplateMutation(
    template._id,
  );
  const { mutateAsync: selectTemplate } = useSelectTemplateMutation(
    template._id,
  );

  const handleRenameTemplate = (templateName: string) => {
    renameTemplate({ templateName });
  };

  const handleSelectTemplate = async () => {
    setIsSelecting(true);
    try {
      if (!context || !context.itemId) {
        console.error('Missing context');
        return;
      }
      const sessionToken = queryClient.getQueryData<{ data: string }>(
        queryKeys.MONDAY_GET_SESSION_TOKEN,
      );
      if (!sessionToken?.data) {
        console.error('Missing session token');
        return;
      }
      await selectTemplate({ envelopeId, itemId: context.itemId });
      await queryClient.invalidateQueries(queryKeys.GET_FILES_ITEM);
      hideGallery();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSelecting(false);
    }
  };

  const handleDuplicateTemplate = async () => {
    setIsDuplicating(true);
    if (!context || !context.itemId) {
      console.error('Missing context');
      return;
    }
    await duplicateTemplate({ envelopeId, itemId: context.itemId });
    await queryClient.invalidateQueries(
      queryKeys.TEMPLATE_GALLERY_USER_TEMPLATES,
    );
    setIsDuplicating(false);
  };

  const handlePreDeleteTemplate = async () => {
    setIsDeleting(true);
    try {
      const response = await validateDeleteTemplate({
        templateId: template._id,
      });
      showDeleteTemplateDialog({
        templateId: template._id,
        isInUse: !response.isAllowed,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePreviewTemplate = async () => {
    const sessionToken = queryClient.getQueryData<{ data: string }>(
      queryKeys.MONDAY_GET_SESSION_TOKEN,
    );
    if (!sessionToken?.data) {
      console.error('Missing session token');
      return;
    }
    if (!context) {
      console.error('Missing context');
      return;
    }
    const url = APPLICATION_ROUTES.TEMPLATE_GALLERY_EDITOR({
      templateId: template._id,
      fileId: template.fileId,
      sessionToken: sessionToken?.data ?? '',
      context,
      isPreview: true,
    });
    await openLinkInTab({ url });
  };

  const handleEditTemplate = async (fileType: string) => {
    if (!context) {
      console.error('Missing context');
      return;
    }
    const sessionToken = queryClient.getQueryData<{ data: string }>(
      queryKeys.MONDAY_GET_SESSION_TOKEN,
    );
    if (!sessionToken?.data) {
      console.error('Missing session token');
      return;
    }

    const isWordEditorRedirectEnabled = isFeatureEnabled(
      FEATURE_FLAGS.WORD_EDITOR_REDIRECT,
    );

    if (!isWordEditorRedirectEnabled) {
      const url = APPLICATION_ROUTES.TEMPLATE_GALLERY_EDITOR({
        templateId: template._id,
        fileId: template.fileId,
        sessionToken: sessionToken?.data,
        context,
      });
      await openLinkInTab({ url });
      return;
    }

    const url =
      fileType === 'pdf'
        ? APPLICATION_ROUTES.TEMPLATE_GALLERY_EDITOR({
            templateId: template._id,
            fileId: template.fileId,
            sessionToken: sessionToken?.data,
            context,
          })
        : APPLICATION_ROUTES.WORD_EDITOR({
            envelopeId,
            templateId: template._id,
            itemId: context.itemId,
            fileId: template.fileId,
            fromTemplateGallery: true,
          });

    await openLinkInTab({ url });
  };

  return (
    <div className="w-full rounded-md border border-layout p-3 transition-all duration-300 hover:shadow-sm">
      <div className="flex gap-5">
        <div className="h-[100px] w-[70px] rounded-sm border border-layout p-1">
          {template?.templateImage ? (
            <img
              src={`${THUMBNAILS_BASE_URL}/${template?.templateImage}`}
              alt={template?.templateName}
              className="w-fill h-full"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Icon icon={Doc} className="h-1/3 w-auto" />
            </div>
          )}
        </div>

        <div className="w-11/12 overflow-hidden whitespace-nowrap text-lg sm:w-9/12">
          <EditableText
            key={template?.templateName}
            onChange={handleRenameTemplate}
            weight={EditableText.weights.BOLD}
            value={template?.templateName}
            type={EditableText.types.TEXT1}
          />
          {isBoardDetailsLoading ? (
            <Skeleton className="h-4 w-full" />
          ) : (
            <div className="overflow-hidden text-ellipsis text-sm italic">
              {boardName}
            </div>
          )}
        </div>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Button
          size={Button.sizes.SMALL}
          kind={Button.kinds.SECONDARY}
          loading={isSelecting}
          onClick={handleSelectTemplate}
        >
          Select
        </Button>
        {/* TODO: Need to look into actual cause for hovering over preview template when select button is clicked */}
        {isSelecting ? (
          <IconButton
            icon={Show}
            size={Button.sizes.SMALL}
            kind={Button.kinds.SECONDARY}
            onClick={handlePreviewTemplate}
          />
        ) : (
          <Tooltip content="Preview Template">
            <IconButton
              icon={Show}
              size={Button.sizes.SMALL}
              kind={Button.kinds.SECONDARY}
              onClick={handlePreviewTemplate}
            />
          </Tooltip>
        )}

        <Tooltip
          content="Edit Template"
          referenceWrapperClassName="flex"
          immediateShowDelay={0}
        >
          <IconButton
            icon={Edit}
            kind={Button.kinds.SECONDARY}
            size={Button.sizes.SMALL}
            onClick={() => handleEditTemplate(template.fileType)}
          />
        </Tooltip>

        <Tooltip
          content="Duplicate Template"
          referenceWrapperClassName="flex"
          immediateShowDelay={0}
        >
          <IconButton
            icon={Duplicate}
            loading={isDuplicating}
            size={Button.sizes.SMALL}
            kind={Button.kinds.SECONDARY}
            onClick={handleDuplicateTemplate}
          />
        </Tooltip>

        <Tooltip
          content="Remove Template"
          referenceWrapperClassName="flex"
          immediateShowDelay={0}
        >
          <IconButton
            icon={Delete}
            loading={isDeleting}
            size={Button.sizes.SMALL}
            kind={Button.kinds.SECONDARY}
            color={Button.colors.NEGATIVE}
            onClick={handlePreDeleteTemplate}
          />
        </Tooltip>
      </div>
    </div>
  );
};

export default TemplateCard;
